/**
 * Token Claims Utility
 *
 * Helpers to extract role/permission claims injected by the Pre Token Generation
 * Lambda (see .kiro/specs/jwt-role-injection/design.md).
 *
 * Cognito custom claims are strings, so `custom:roles` and `custom:permissions`
 * are JSON-stringified arrays. Parsing is wrapped in try/catch so missing or
 * malformed claims fall back to empty arrays — callers then fall back to the
 * GraphQL query path.
 */

import { Auth } from 'aws-amplify';

export interface TokenClaims {
  roles: string[];
  permissions: string[];
}

const EMPTY: TokenClaims = { roles: [], permissions: [] };

function safeParseArray(value: unknown): string[] {
  if (typeof value !== 'string' || value.length === 0) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * Extract roles/permissions from a decoded ID token payload.
 */
export function parseClaimsFromPayload(payload: Record<string, unknown> | null | undefined): TokenClaims {
  if (!payload) return EMPTY;
  return {
    roles: safeParseArray(payload['custom:roles']),
    permissions: safeParseArray(payload['custom:permissions']),
  };
}

/**
 * Decode a raw JWT ID token string and extract roles/permissions.
 * Used in OAuth callback paths where we already hold the token string.
 */
export function parseClaimsFromIdTokenString(idToken: string | null | undefined): TokenClaims {
  if (!idToken) return EMPTY;
  try {
    const payload = JSON.parse(atob(idToken.split('.')[1]));
    return parseClaimsFromPayload(payload);
  } catch {
    return EMPTY;
  }
}

/**
 * Read roles/permissions from the current Amplify session's ID token.
 * Returns empty arrays if no session exists or claims are missing.
 */
export async function getClaimsFromCurrentSession(): Promise<TokenClaims> {
  try {
    const session = await Auth.currentSession();
    const payload = session.getIdToken().decodePayload() as Record<string, unknown>;
    return parseClaimsFromPayload(payload);
  } catch {
    return EMPTY;
  }
}
